import {createAction, props} from '@ngrx/store';
import {FakturierungsbelegFormComponentMode} from '../../types/fakturierungsbeleg-form-component-mode.type';
import {
  BelegDTO,
  CreateNachlaufzeileRequestDTO,
  PositionDTO,
  ProblemDTO,
  ProduktDTO,
  UpdateNachlaufzeileRequestDTO
} from '../../openapi/fakturierung-openapi';
import {PositionMengeMode} from '../../interfaces/position-menge-modes.interface';
import {PositionEinzelpreisNettoMode} from '../../interfaces/position-einzelpreisnetto-mode.interface';
import {PositionBeschreibungMode} from '../../interfaces/position-beschreibung-mode.interface';
import {DateSelectingMode, MappedHttpErrorResponse} from '@adnova/jf-ng-components';
import {UpdatePosition} from '../../modules/components/fakturierungsbelege-form/beleg-table/beleg-table.component';
import {Referenzfeld} from '../../util/referenzfelder';


export class FakturierungsbelegFormActions {

  private static readonly prefix = '[Fakturierungsbeleg Form] ';

  public static reset = createAction(
    this.prefix + 'Reset this context.',
  );

  public static updateFakturierungsbelegDto = createAction(
    this.prefix + 'Update the selected fakturierungsbeleg data.',
    props<{
      fakturierungsbelegDto: BelegDTO,
    }>(),
  );

  // INFO: Referenzfeld actions.
  public static hydrateReferenzfelder = createAction(
    this.prefix + 'Derive visibility of the referenzfelder from the loaded beleg.',
    props<{
      belegDto: BelegDTO,
    }>(),
  );

  public static toggleReferenzfeld = createAction(
    this.prefix + 'Toggle visibility of a referenzfeld.',
    props<{
      feld: Referenzfeld,
      sichtbar: boolean,
    }>(),
  );

  public static resetReferenzfelderSichtbarkeit = createAction(
    this.prefix + 'Hide all referenzfelder.',
  );

  public static changeKontaktMode = createAction(
    this.prefix + 'Change kontakt-mode.',
    props<{
      mode: FakturierungsbelegFormComponentMode,
    }>(),
  );

  // INFO: Nachlaufzeile actions.
  public static addNachlaufzeile = createAction(
    this.prefix + 'Add Nachlaufzeile',
    props<{
      createNachlaufzeileRequestDTO: CreateNachlaufzeileRequestDTO,
      belegId: string,
    }>(),
  );

  public static addNachlaufzeileSuccess = createAction(
    '[Fakturierungsbeleg Form] Add Nachlaufzeile Success',
    props<{
      belegId: string,
    }>(),
  );

  public static addNachlaufzeileFailure = createAction(
    '[Fakturierungsbeleg Form] Add Nachlaufzeile Failure',
    props<{
      error: MappedHttpErrorResponse<ProblemDTO>,
    }>(),
  );

  public static removeNachlaufzeile = createAction(
    this.prefix + 'Remove Nachlaufzeile',
    props<{
      nachlaufzeileId: string,
      belegId: string,
    }>(),
  );

  public static removeNachlaufzeileSuccess = createAction(
    '[Fakturierungsbeleg Form] Remove Nachlaufzeile Success',
    props<{
      belegId: string,
    }>(),
  );

  public static removeNachlaufzeileFailure = createAction(
    '[Fakturierungsbeleg Form] Remove Nachlaufzeile Failure',
    props<{
      error: any,
    }>(),
  );

  public static updateNachlaufzeile = createAction(
    '[Fakturierungsbeleg Form] Update Nachlaufzeile',
    props<{
      nachlaufzeileId: string,
      updateNachlaufzeileRequestDTO: UpdateNachlaufzeileRequestDTO,
      belegId: string,
    }>(),
  );

  public static updateNachlaufzeileSuccess = createAction(
    '[Fakturierungsbeleg Form] Update Nachlaufzeile Success',
    props<{
      belegId: string,
    }>(),
  );

  public static updateNachlaufzeileFailure = createAction(
    '[Fakturierungsbeleg Form] Update Nachlaufzeile Failure',
    props<{
      error: any,
    }>(),
  );

  public static changeVorlaufzeileMode = createAction(
    this.prefix + 'Change Vorlaufzeile Mode.',
    props<{
      mode: FakturierungsbelegFormComponentMode,
    }>(),
  );

  // INFO : Position actions
  public static addPosition = createAction( // TODO: add loading property. so loading spinner can be displayed.
    this.prefix + 'Add Position to the Fakturierungsbeleg-Form.',
    props<{
      produktDto: ProduktDTO,
    }>(),
  );

  public static addPositionSuccess = createAction(
    this.prefix + 'Added Position to the Fakturierungsbeleg-Form successfully.',
    props<{
      newlyCreatedPosition: PositionDTO,
    }>(),
  );

  public static addPositionFailure = createAction(
    this.prefix + 'Adding Position to the Fakturierungsbeleg-Form failed.',
    props<{ error: MappedHttpErrorResponse<ProblemDTO> }>(),
  );

  public static deletePosition = createAction(
    this.prefix + 'delete Position',
    props<{ positionId: string }>(),
  );

  public static deletePositionSuccess = createAction(
    this.prefix + 'deleted Position successfully.',
    props<{ positionId: string }>(),
  );

  public static deletePositionFailure = createAction(
    this.prefix + 'deleting Position failed.',
    props<{ error: MappedHttpErrorResponse }>(),
  );

  public static changePositionMengeMode = createAction(
    this.prefix + 'Change Position Menge Mode.',
    props<{
      positionMengeMode: PositionMengeMode,
    }>(),
  );

  // INFO: one same action to update Menge or Einzelpreisnetto, because the same payload is required.
  public static updatePosition = createAction( // TODO: add loading state to this operation.
    this.prefix + 'Update Position.',
    props<{
      updatePositionObj: UpdatePosition,
    }>(),
  );

  public static updatePositionSuccess = createAction(
    this.prefix + 'Update Position Success',
    props<{ newlyUpdatedPosition: PositionDTO }>(),
  );

  public static updatePositionFailure = createAction(
    this.prefix + 'Update Position failed.',
    props<{ positionId: string, error: MappedHttpErrorResponse<ProblemDTO> }>(),
  );

  public static fetchPositionen = createAction(
    this.prefix + 'Fetch Positionen of current Beleg.',
    props<{ belegId: string }>(),
  );

  public static fetchPositionenSuccess = createAction(
    this.prefix + 'Fetched Positionen of current Beleg successfully',
    props<{ positionen: PositionDTO[] }>(),
  );

  public static fetchPositionenFailure = createAction(
    this.prefix + 'Fetched Positionen of current Beleg failed',
    props<{ error: MappedHttpErrorResponse }>(),
  );

  public static changePositionEinzelpreisNettoMode = createAction(
    this.prefix + 'Change Position EinzelpreisNetto Mode.',
    props<{
      positionEinzelpreisNettoMode: PositionEinzelpreisNettoMode,
    }>(),
  );

  public static changePositionBeschreibungMode = createAction(
    this.prefix + 'Change Position Beschreibung Mode.',
    props<{
      positionBeschreibungMode: PositionBeschreibungMode,
    }>(),
  );

  public static changeAddPositionMode = createAction(
    this.prefix + 'Change Add-Produkt Mode.',
    props<{
      mode: FakturierungsbelegFormComponentMode,
    }>(),
  );

  // INFO: Date actions.
  public static changeBelegdatumMode = createAction(
    this.prefix + 'Change belegdatum mode.',
    props<{
      mode: FakturierungsbelegFormComponentMode,
    }>(),
  );

  public static changeLeistungsLieferDatumControlMode = createAction(
    this.prefix + 'Change leistungs-liefer-datum control mode.',
    props<{
      mode: FakturierungsbelegFormComponentMode,
    }>(),
  );

  public static changeLeistungsLieferDatumDateMode = createAction(
    this.prefix + 'Change leistungs-liefer-datum date selecting mode.',
    props<{
      mode: DateSelectingMode,
    }>(),
  );


  public static setLeistungsLieferDatumRange = createAction(
    this.prefix + 'Set leistungs-liefer-datum range date.',
    props<{
      datumVon: string,
      datumBis: string,
    }>(),
  );

  // INFO: Logo actions

  public static uploadLogo = createAction(
    this.prefix + 'Uploads the logo.',
    props<{
      objectUrl: string,
    }>(),
  );

  public static uploadLogoSuccess = createAction(
    this.prefix + 'Upload logo successfully.',
  );

  public static uploadLogoFailure = createAction(
    this.prefix + 'Upload logo failed.',
    props<{ error: MappedHttpErrorResponse }>(),
  );

  public static getLogo = createAction(
    this.prefix + 'Loads the logo.',
    props<{
      einstellungId: string,
      einstellungRevision: number,
    }>(),
  );

  public static updateLogoUrl = createAction(
    this.prefix + 'Loaded logo successfully.',
    props<{
      objectUrl?: string,
    }>()
  );

  public static getLogoFailure = createAction(
    this.prefix + 'Load logo failed.',
    props<{ error: MappedHttpErrorResponse }>(),
  );

  public static deleteLogo = createAction(
    this.prefix + 'Delete logo.',
  );

  public static deleteLogoSuccess = createAction(
    this.prefix + 'Deleted logo successfully.',
  );

  public static deleteLogoFailure = createAction(
    this.prefix + 'Delete logo failed.',
    props<{ error: MappedHttpErrorResponse }>(),
  );

  public static releaseLogoFromMemory = createAction(
    this.prefix + 'Release logo from browser memory.',
    props<{ objectUrl: string }>(),
  );

  public static redirectToForm = createAction(
    this.prefix + 'Redirect to form.',
    props<{
      betriebId: string,
      belegId: string,
    }>()
  );
}
